import React, { useState, useMemo, useEffect } from 'react';
import { X, Trophy, Plus, AlertCircle, RotateCcw } from 'lucide-react';
import { useProfile } from '../../context/ProfileContext';
import MediaCaptureMenu from '../MediaCaptureMenu';
import PasteUrlInput from '../PasteUrlInput';
import AttachmentTile from '../AttachmentTile';
import { fileToPayload, urlToPayload } from '../../lib/mediaCapture';
import MultiSelectPills from './MultiSelectPills';
import ChipInput from './ChipInput';
import TechniqueRow from './TechniqueRow';
import {
  BJJ_CLASS_FORMATS,
  BJJ_FOCUS_MODES,
  BJJ_SPARRING_TRIGGERS,
  BJJ_FELT_SCALE,
  newTechniqueRow,
  newSparring,
  newReflection,
  newInjury,
  inferPlayerSide,
} from '../../data/combatives';

const DRAFT_KEY = 'enferno_bjj_session_draft_v1';

// Tiny relative-time helper for the "restored from X" banner.
// Goes to "a moment ago" / "5 min ago" / "2 hr ago" / "yesterday" / "Jun 4".
const formatTimeAgo = (iso) => {
  if (!iso) return 'earlier';
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  if (ms < 60_000) return 'a moment ago';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * BJJLogDialog — rich session journal for BJJ. Routed in Training.jsx when
 * art.id === 'bjj'. Other arts continue to use the generic LogSessionDialog.
 *
 * Schema written to addTrainingSession (lock):
 *   {
 *     date, duration, attachments, tz_offset_minutes, notes,    // universal
 *     classFormat: ['Gi','No-Gi'],                              // multi-select
 *     focusModes: [...],                                        // multi-select
 *     coaches: [...],                                           // multi
 *     beltSnapshot: { rankId, stripes },                        // auto
 *     intent: '',                                               // pre-session goal
 *     techniques: [...],                                        // rich
 *     sparring: { ... } | null,                                 // when Live Rolling
 *     reflection: { intentHit, win, gap, nextFocus },
 *     injury: { bodyPart, note } | null,
 *   }
 */
const BJJLogDialog = ({ art, onClose, onSave, initialData = null }) => {
  const { profile, training } = useProfile();
  const beltSnapshot = (profile?.ranks?.bjj) || null;
  const isEditMode = !!initialData;

  // Autocomplete sources — flatten unique values from prior sessions so the
  // user's vocabulary grows session over session. Honors the
  // "user-defined value" axiom: we don't impose a taxonomy, we surface
  // their own past entries.
  const autocompleteSuggestions = useMemo(() => {
    const techniqueNames = new Set();
    const coachNames = new Set();
    const partnerNames = new Set();
    (training?.sessions || []).forEach((s) => {
      // Skip the session being edited so its own values don't show up as
      // suggestions for themselves.
      if (initialData && s.id === initialData.id) return;
      (s.techniques || []).forEach((t) => { if (t?.name) techniqueNames.add(t.name); });
      (s.coaches || []).forEach((c) => { if (c) coachNames.add(c); });
      (s.sparring?.partners || []).forEach((p) => { if (p) partnerNames.add(p); });
    });
    return {
      techniques: Array.from(techniqueNames).sort(),
      coaches: Array.from(coachNames).sort(),
      partners: Array.from(partnerNames).sort(),
    };
  }, [training?.sessions, initialData]);

  const todayLocal = useMemo(() => {
    const d = new Date();
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOff).toISOString().slice(0, 10);
  }, []);
  const minLocal = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOff).toISOString().slice(0, 10);
  }, []);

  // Build initial form state. Edit mode prefills from initialData and
  // converts numeric sparring counts back to strings (so the placeholder
  // UX works the same in edit mode as in create mode).
  const buildInitialForm = () => {
    if (!initialData) {
      return {
        date: todayLocal,
        duration: '60',
        classFormat: [],
        focusModes: [],
        coaches: [],
        intent: '',
        techniques: [],
        sparring: newSparring(),
        reflection: newReflection(),
        injuryEnabled: false,
        injury: newInjury(),
        notes: '',
        attachments: [],
      };
    }
    // Edit mode: hydrate from saved record
    const sessionDate = initialData.date ? new Date(initialData.date) : new Date();
    const tzOff = sessionDate.getTimezoneOffset() * 60000;
    const dateStr = new Date(sessionDate.getTime() - tzOff).toISOString().slice(0, 10);
    const numToStr = (n) => (n === 0 || n === undefined || n === null) ? '' : String(n);
    const savedSparring = initialData.sparring;
    return {
      date: dateStr,
      duration: String(initialData.duration ?? '60'),
      classFormat: initialData.classFormat || [],
      focusModes: initialData.focusModes || [],
      coaches: initialData.coaches || [],
      intent: initialData.intent || '',
      techniques: initialData.techniques || [],
      sparring: savedSparring ? {
        rolls: numToStr(savedSparring.rolls),
        partners: savedSparring.partners || [],
        subsLanded: numToStr(savedSparring.subsLanded),
        subsCaught: numToStr(savedSparring.subsCaught),
        sweepsLanded: numToStr(savedSparring.sweepsLanded),
        sweepsAllowed: numToStr(savedSparring.sweepsAllowed),
        passesLanded: numToStr(savedSparring.passesLanded),
        passesAllowed: numToStr(savedSparring.passesAllowed),
        takedownsLanded: numToStr(savedSparring.takedownsLanded),
        takedownsConceded: numToStr(savedSparring.takedownsConceded),
        feltScale: savedSparring.feltScale ?? 3,
        note: savedSparring.note || '',
      } : newSparring(),
      reflection: initialData.reflection || newReflection(),
      injuryEnabled: !!initialData.injury,
      injury: initialData.injury || newInjury(),
      notes: initialData.notes || '',
      attachments: initialData.attachments || [],
    };
  };

  const [form, setFormState] = useState(buildInitialForm);
  const [error, setError] = useState('');
  const [restoredFromDraft, setRestoredFromDraft] = useState(null);  // ISO timestamp or null
  // Dirty tracking — flips true on first form change, never goes back.
  // Drives the confirm-on-close prompt for the backdrop / X / Cancel.
  const [dirty, setDirty] = useState(false);
  // In-app confirmation modal trigger. Window.confirm was suppressed on
  // mobile browsers (especially Samsung Internet) — replaced with a
  // styled modal that's guaranteed to render. true = visible.
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Every form change marks dirty AND writes the draft to localStorage
  // synchronously in the same call. Doing this in a useEffect was racy —
  // first-render restore could trigger the effect with stale state, and
  // mobile browsers occasionally batched the effect with the next render
  // so the most recent edit didn't reach localStorage before navigation.
  // Synchronous write is unconditionally reliable.
  //
  // Edit mode (initialData present) skips draft autosave entirely — the
  // draft slot belongs to in-progress new-session work; saving an edit
  // mid-flight to that slot would clobber any unsaved new session.
  const setForm = (next) => {
    setDirty(true);
    setFormState((prev) => {
      const updated = typeof next === 'function' ? next(prev) : next;
      if (!isEditMode) {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({
            savedAt: new Date().toISOString(),
            form: updated,
          }));
        } catch { /* quota or private-mode — fail silently */ }
      }
      return updated;
    });
  };

  // Restore an in-progress draft if one exists in localStorage. Skipped in
  // edit mode — the form was already prefilled from initialData.
  useEffect(() => {
    if (isEditMode) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.form) {
        setFormState(draft.form);
        setRestoredFromDraft(draft.savedAt || null);
        setDirty(true);
      }
    } catch { /* corrupt JSON — ignore, will be overwritten on next save */ }
  }, [isEditMode]);

  // Discard the draft after a successful save OR explicit "Start over."
  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setRestoredFromDraft(null);
  };

  // Reset to a blank form. Used by the "Start over" button on the restored
  // banner. Clears the draft and dirty flag so the autosave effect doesn't
  // immediately rewrite the empty form to localStorage.
  const startOver = () => {
    clearDraft();
    setFormState({
      date: todayLocal,
      duration: '60',
      classFormat: [],
      focusModes: [],
      coaches: [],
      intent: '',
      techniques: [],
      sparring: newSparring(),
      reflection: newReflection(),
      injuryEnabled: false,
      injury: newInjury(),
      notes: '',
      attachments: [],
    });
    setDirty(false);
  };

  // Backdrop / X / Cancel all route through this. If the form is empty
  // we close immediately. If dirty, we show the in-app confirm modal.
  // Old version used window.confirm which Samsung Internet + some mobile
  // browsers suppress entirely — that left the user stuck unable to close
  // the dialog.
  const handleClose = () => {
    if (!dirty) {
      onClose();
      return;
    }
    setConfirmDiscard(true);
  };

  const confirmDiscardYes = () => {
    if (!isEditMode) clearDraft();
    setConfirmDiscard(false);
    onClose();
  };
  const confirmDiscardNo = () => setConfirmDiscard(false);

  const showSparring = form.focusModes.some((m) => BJJ_SPARRING_TRIGGERS.includes(m));

  const updateTechnique = (idx, next) => {
    setForm((prev) => ({
      ...prev,
      techniques: prev.techniques.map((t, i) => (i === idx ? next : t)),
    }));
  };
  const removeTechnique = (idx) => {
    setForm((prev) => ({ ...prev, techniques: prev.techniques.filter((_, i) => i !== idx) }));
  };
  const addTechnique = () => {
    // new techniques prepend to the top of the list so the
    // user always sees the newly-added blank row without scrolling.
    // Founder direction: newest on top is the standard pattern across
    // virtually every other journal/notes app, and adding-to-bottom
    // hides the new row in long sessions.
    setForm((prev) => ({ ...prev, techniques: [newTechniqueRow(), ...prev.techniques] }));
  };

  const updSparring = (patch) => setForm((p) => ({ ...p, sparring: { ...p.sparring, ...patch } }));
  const updReflection = (patch) => setForm((p) => ({ ...p, reflection: { ...p.reflection, ...patch } }));
  const updInjury = (patch) => setForm((p) => ({ ...p, injury: { ...p.injury, ...patch } }));

  // Add a captured/picked media attachment. The MediaCaptureMenu now
  // hands us a fully-formed payload from mediaCapture.js — already
  // persisted to device storage (native) or held as a Blob URL (web).
  // We just push it onto form.attachments. No size cap because the binary
  // never enters our cloud — it lives on the user's device.
  const addAttachment = (payload) => {
    setError('');
    if (!payload) return;
    // Legacy compatibility: if a raw File slipped through (e.g., older
    // caller code), convert it first.
    if (payload instanceof Blob && !('kind' in payload)) {
      fileToPayload(payload).then((p) => {
        setForm((prev) => ({ ...prev, attachments: [...prev.attachments, p] }));
      });
      return;
    }
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, payload] }));
  };

  const addExternalUrl = (url) => {
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, urlToPayload(url)] }));
  };
  const removeAtt = (i) => setForm({ ...form, attachments: form.attachments.filter((_, idx) => idx !== i) });

  const handleSave = () => {
    const isToday = form.date === todayLocal;
    const sessionDate = isToday
      ? new Date().toISOString()
      : new Date(`${form.date}T12:00:00`).toISOString();

    // Sparring stat fields are stored as strings (placeholder UX) but we
    // ship them as numbers. Empty strings become 0.
    const sparringPayload = showSparring ? {
      rolls: Number(form.sparring.rolls) || 0,
      partners: form.sparring.partners,
      subsLanded: Number(form.sparring.subsLanded) || 0,
      subsCaught: Number(form.sparring.subsCaught) || 0,
      sweepsLanded: Number(form.sparring.sweepsLanded) || 0,
      sweepsAllowed: Number(form.sparring.sweepsAllowed) || 0,
      passesLanded: Number(form.sparring.passesLanded) || 0,
      passesAllowed: Number(form.sparring.passesAllowed) || 0,
      takedownsLanded: Number(form.sparring.takedownsLanded) || 0,
      takedownsConceded: Number(form.sparring.takedownsConceded) || 0,
      feltScale: form.sparring.feltScale,
      note: form.sparring.note,
    } : null;

    onSave({
      date: sessionDate,
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: Number(form.duration) || 60,
      // legacy `focus` derived from first focus mode so existing display
      // code in Training.jsx (session card preview) keeps rendering until
      // Session 1b updates it to read journal fields directly.
      focus: form.focusModes[0] || 'Drilling',
      notes: form.notes,
      attachments: form.attachments,
      // new BJJ-specific journal payload
      classFormat: form.classFormat,
      focusModes: form.focusModes,
      coaches: form.coaches,
      beltSnapshot,
      intent: form.intent,
      // Auto-derive playerSide from the position picker on save.
      // playerSide is a DERIVED field — never user-input. Game Maps will
      // read this for the top-game/bottom-game split. Same logic applied
      // to the optional `nextPosition` edge data.
      techniques: form.techniques.map((t) => ({
        ...t,
        playerSide: inferPlayerSide(t.position),
        nextPosition: t.nextPosition
          ? { ...t.nextPosition, playerSide: inferPlayerSide(t.nextPosition) }
          : null,
      })),
      sparring: sparringPayload,
      reflection: form.reflection,
      injury: form.injuryEnabled ? form.injury : null,
    });
    // Successful create-save — discard the in-progress draft. In edit
    // mode we never wrote to the draft slot, so don't touch it (it may
    // belong to an unsaved new-session draft).
    if (!isEditMode) clearDraft();
  };

  const sectionLabel = 'text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block';
  const sectionHeading = 'text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-semibold mb-3';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={handleClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="bjj-log-dialog">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-1">{isEditMode ? 'Edit session' : 'Log session'} · {art.shortName}</div>
            <div className="font-display text-3xl tracking-wide leading-none">Combatives journal</div>
          </div>
          <button onClick={handleClose} aria-label="Close" className="text-enf-text-muted hover:text-enf-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Restored-from-draft banner — only renders when a draft was loaded
            on mount. "Start over" lets the user discard if it's stale. */}
        {restoredFromDraft && (
          <div className="mb-5 p-3 rounded-md bg-enf-role-info/10 border border-enf-role-info/30 flex items-center justify-between gap-3" data-testid="bjj-draft-restored-banner">
            <div className="flex items-center gap-2 min-w-0">
              <RotateCcw className="w-4 h-4 text-enf-role-info flex-shrink-0" />
              <span className="text-xs text-enf-text-secondary truncate">
                Restored unsaved draft from {formatTimeAgo(restoredFromDraft)}
              </span>
            </div>
            <button
              type="button"
              onClick={startOver}
              data-testid="bjj-draft-start-over"
              className="text-xs font-semibold text-enf-role-warning hover:text-enf-text-primary flex-shrink-0"
            >
              Start over
            </button>
          </div>
        )}

        {/* INTENT — the differentiator, top of the form, visually emphasized */}
        <div className="mb-6 p-4 rounded-lg bg-enf-accent/10 border border-enf-accent-fg/40">
          <label className="text-[11px] uppercase tracking-[0.25em] text-enf-accent-fg font-semibold mb-2 block">
            Pre-session intent · what are you working today?
          </label>
          <input
            type="text"
            value={form.intent}
            onChange={(e) => setForm({ ...form, intent: e.target.value })}
            placeholder="e.g. Half-guard knee shield against bigger partners"
            data-testid="bjj-intent"
            className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-accent-fg/30 rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
          />
        </div>

        {/* SESSION HEADER */}
        <div className="mb-6 space-y-4">
          <div className={sectionHeading}>Session</div>
          {/* Date + Duration */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={sectionLabel}>Date</label>
              <input
                type="date"
                value={form.date}
                max={todayLocal}
                min={minLocal}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="bjj-date"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
              />
            </div>
            <div>
              <label className={sectionLabel}>Duration (min)</label>
              <input
                type="number"
                inputMode="numeric"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                data-testid="bjj-duration"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
              />
            </div>
          </div>

          {/* Class format — multi */}
          <div>
            <label className={sectionLabel}>Class format · select one or both</label>
            <MultiSelectPills
              options={BJJ_CLASS_FORMATS}
              value={form.classFormat}
              onChange={(v) => setForm({ ...form, classFormat: v })}
              testId="bjj-classformat"
            />
          </div>

          {/* Focus modes — multi */}
          <div>
            <label className={sectionLabel}>Focus modes · select all that apply</label>
            <MultiSelectPills
              options={BJJ_FOCUS_MODES}
              value={form.focusModes}
              onChange={(v) => setForm({ ...form, focusModes: v })}
              testId="bjj-focusmode"
            />
          </div>

          {/* Coaches — multi via chip input */}
          <div>
            <label className={sectionLabel}>Coaches · add one or more</label>
            <ChipInput
              value={form.coaches}
              onChange={(v) => setForm({ ...form, coaches: v })}
              placeholder="e.g. Professor Tom"
              suggestions={autocompleteSuggestions.coaches}
              testId="bjj-coach"
            />
          </div>
        </div>

        {/* TECHNIQUES */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className={sectionHeading + ' mb-0'}>Techniques worked · {form.techniques.length}</div>
            <button
              type="button"
              onClick={addTechnique}
              data-testid="bjj-add-technique"
              className="px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-xs font-semibold text-enf-text-secondary hover:text-enf-text-primary transition-colors flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add technique
            </button>
          </div>
          {form.techniques.length === 0 ? (
            <div className="text-center py-6 text-xs text-enf-text-muted border border-dashed border-enf-surface-border rounded-lg">
              No techniques logged yet. Tap &ldquo;Add technique&rdquo; to start.
            </div>
          ) : (
            <div className="space-y-2">
              {form.techniques.map((t, i) => (
                <TechniqueRow
                  key={i}
                  value={t}
                  onChange={(next) => updateTechnique(i, next)}
                  onRemove={() => removeTechnique(i)}
                  index={i}
                  nameSuggestions={autocompleteSuggestions.techniques}
                />
              ))}
            </div>
          )}
        </div>

        {/* SPARRING — conditional */}
        {showSparring && (
          <div className="mb-6 p-4 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid="bjj-sparring-section">
            <div className={sectionHeading}>Sparring</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={sectionLabel}>Rolls</label>
                <input type="number" inputMode="numeric" min="0" value={form.sparring.rolls}
                  onChange={(e) => updSparring({ rolls: e.target.value })}
                  placeholder="0"
                  data-testid="bjj-sparring-rolls"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
              </div>
              <div>
                <label className={sectionLabel}>Felt</label>
                <select value={form.sparring.feltScale}
                  onChange={(e) => updSparring({ feltScale: Number(e.target.value) })}
                  data-testid="bjj-sparring-felt"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg">
                  {BJJ_FELT_SCALE.map((s) => (
                    <option key={s.value} value={s.value}>{s.value} — {s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className={sectionLabel}>Partners</label>
              <ChipInput
                value={form.sparring.partners}
                onChange={(v) => updSparring({ partners: v })}
                placeholder="e.g. Mike (blue)"
                suggestions={autocompleteSuggestions.partners}
                testId="bjj-sparring-partner"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <StatField label="Subs landed" value={form.sparring.subsLanded} onChange={(v) => updSparring({ subsLanded: v })} testId="bjj-stat-subs-landed" />
              <StatField label="Subs caught in" value={form.sparring.subsCaught} onChange={(v) => updSparring({ subsCaught: v })} testId="bjj-stat-subs-caught" />
              <StatField label="Sweeps landed" value={form.sparring.sweepsLanded} onChange={(v) => updSparring({ sweepsLanded: v })} testId="bjj-stat-sweeps-landed" />
              <StatField label="Sweeps allowed" value={form.sparring.sweepsAllowed} onChange={(v) => updSparring({ sweepsAllowed: v })} testId="bjj-stat-sweeps-allowed" />
              <StatField label="Passes landed" value={form.sparring.passesLanded} onChange={(v) => updSparring({ passesLanded: v })} testId="bjj-stat-passes-landed" />
              <StatField label="Passes allowed" value={form.sparring.passesAllowed} onChange={(v) => updSparring({ passesAllowed: v })} testId="bjj-stat-passes-allowed" />
              <StatField label="Takedowns landed" value={form.sparring.takedownsLanded} onChange={(v) => updSparring({ takedownsLanded: v })} testId="bjj-stat-td-landed" />
              <StatField label="Takedowns conceded" value={form.sparring.takedownsConceded} onChange={(v) => updSparring({ takedownsConceded: v })} testId="bjj-stat-td-conceded" />
            </div>
            <div className="mt-3">
              <label className={sectionLabel}>Sparring note</label>
              <textarea value={form.sparring.note}
                onChange={(e) => updSparring({ note: e.target.value })}
                rows={2}
                placeholder="What worked, what failed, partner notes..."
                data-testid="bjj-sparring-note"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
            </div>
          </div>
        )}

        {/* REFLECTION */}
        <div className="mb-6 p-4 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid="bjj-reflection-section">
          <div className={sectionHeading}>Reflection</div>
          {/* Intent callback — only renders when intent was set */}
          {form.intent.trim() && (
            <div className="mb-3 p-3 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30">
              <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Your intent today was</div>
              <div className="text-sm text-enf-text-primary italic mb-2">&ldquo;{form.intent}&rdquo;</div>
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5">Did you accomplish it?</div>
              <div className="flex gap-1.5">
                {[
                  { v: true, label: 'Yes' },
                  { v: 'partial', label: 'Partial' },
                  { v: false, label: 'No' },
                ].map((opt) => (
                  <button
                    key={String(opt.v)}
                    type="button"
                    onClick={() => updReflection({ intentHit: opt.v })}
                    data-testid={`bjj-intent-hit-${String(opt.v)}`}
                    aria-pressed={form.reflection.intentHit === opt.v}
                    className={`px-3 py-1.5 text-xs rounded-md border font-semibold transition-colors ${
                      form.reflection.intentHit === opt.v
                        ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                        : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className={sectionLabel}>Biggest win</label>
              <textarea value={form.reflection.win}
                onChange={(e) => updReflection({ win: e.target.value })}
                rows={2}
                placeholder="What worked today?"
                data-testid="bjj-reflection-win"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
            </div>
            <div>
              <label className={sectionLabel}>Biggest gap</label>
              <textarea value={form.reflection.gap}
                onChange={(e) => updReflection({ gap: e.target.value })}
                rows={2}
                placeholder="What didn't work? What got exposed?"
                data-testid="bjj-reflection-gap"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
            </div>
            <div>
              <label className={sectionLabel}>Focus for next session</label>
              <input type="text" value={form.reflection.nextFocus}
                onChange={(e) => updReflection({ nextFocus: e.target.value })}
                placeholder="What you'll work next time"
                data-testid="bjj-reflection-next"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
          </div>
        </div>

        {/* INJURY — collapsed by default behind a checkbox */}
        <div className="mb-6">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.injuryEnabled}
              onChange={(e) => setForm({ ...form, injuryEnabled: e.target.checked })}
              data-testid="bjj-injury-toggle"
              className="w-4 h-4 rounded border-enf-surface-border bg-enf-surface-mid"
            />
            <span className="text-sm text-enf-text-secondary">I tweaked something today (log an injury)</span>
          </label>
          {form.injuryEnabled && (
            <div className="mt-3 p-3 rounded-md bg-enf-role-warning/5 border border-enf-role-warning/30 space-y-2">
              <div>
                <label className={sectionLabel}>Body part</label>
                <input type="text" value={form.injury.bodyPart}
                  onChange={(e) => updInjury({ bodyPart: e.target.value })}
                  placeholder="e.g. left shoulder, right ankle"
                  data-testid="bjj-injury-bodypart"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
              </div>
              <div>
                <label className={sectionLabel}>Note</label>
                <textarea value={form.injury.note}
                  onChange={(e) => updInjury({ note: e.target.value })}
                  rows={2}
                  placeholder="What happened, how it feels"
                  data-testid="bjj-injury-note"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
              </div>
            </div>
          )}
        </div>

        {/* GENERAL NOTES + ATTACHMENTS */}
        <div className="mb-6 space-y-4">
          <div>
            <label className={sectionLabel}>General notes</label>
            <textarea value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Anything else worth remembering"
              data-testid="bjj-general-notes"
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
          </div>
          <div>
            <label className={sectionLabel}>Attachments ({form.attachments.length})</label>
            <div className="space-y-2">
              <MediaCaptureMenu
                onCapture={(payload) => addAttachment(payload)}
                accept="both"
                multiple={false}
                label="Add photo or video"
                testId="bjj-attachment-add"
                recordingEtiquette
                className="w-full p-3 border border-dashed border-enf-surface-border hover:border-enf-role-info rounded-md text-sm text-enf-text-secondary hover:text-enf-role-info transition-colors flex items-center justify-center gap-2"
              />
              <PasteUrlInput onAdd={addExternalUrl} testId="bjj-paste-url" />
            </div>
            {error && (
              <div className="text-xs text-enf-role-warning mt-2 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            {form.attachments.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {form.attachments.map((a, i) => (
                  <AttachmentTile key={i} att={a} onRemove={() => removeAtt(i)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ACTIONS */}
        <div className="flex gap-3">
          <button onClick={handleClose} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors" data-testid="bjj-cancel">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="bjj-save">
            <Trophy className="w-4 h-4" /> Save session
          </button>
        </div>
      </div>

      {/* In-app discard-draft confirmation modal. Rendered as a sibling
          of the dialog content (still inside the outer backdrop) so its
          higher z-index puts it above. Stops propagation to keep clicks
          on its own backdrop from re-triggering handleClose. */}
      {confirmDiscard && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
          onClick={(e) => { e.stopPropagation(); confirmDiscardNo(); }}
          data-testid="bjj-confirm-discard-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5"
            role="dialog"
            aria-labelledby="bjj-confirm-discard-title"
          >
            <div id="bjj-confirm-discard-title" className="font-display text-xl tracking-wide leading-none mb-2">Discard this draft?</div>
            <div className="text-sm text-enf-text-secondary mb-5">Your in-progress session will be lost.</div>
            <div className="flex gap-3">
              <button
                onClick={confirmDiscardNo}
                data-testid="bjj-confirm-discard-no"
                className="flex-1 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors"
              >
                Keep editing
              </button>
              <button
                onClick={confirmDiscardYes}
                data-testid="bjj-confirm-discard-yes"
                className="flex-1 py-2 bg-enf-role-warning/15 hover:bg-enf-role-warning/25 border border-enf-role-warning/50 text-enf-role-warning rounded-md text-sm font-semibold transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Small stat input — used 8x in the sparring grid. Stores value as a
// string for proper placeholder UX (empty input shows "0" placeholder,
// single-digit entry doesn't snap back to "0"). Save handler coerces to
// Number at write time.
const StatField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">{label}</label>
    <input
      type="number"
      inputMode="numeric"
      min="0"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="0"
      data-testid={testId}
      className="w-full px-3 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm text-center focus:outline-none focus:border-enf-accent-fg"
    />
  </div>
);

export default BJJLogDialog;
